import { Component, DestroyRef, inject, Input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-mat-resizeable-panel',
  imports: [
    DragDropModule,
  ],
  templateUrl: './mat-resizeable-panel.html',
  styleUrl: './mat-resizeable-panel.scss',
})
export class MatResizeablePanel implements OnInit {
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  /**
   * Minimum width in percentage of window.innerWidth
   */
  @Input() minWidth = 20;   // default = 20%

  /**
   * Maximum width in percentage of window.innerWidth
   */
  @Input() maxWidth = 60;   // default = 60%

  protected currentWidth = signal(250);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.observeWindowResize();
    }
  }

  ngOnInit() {
    this.setInitialWidth();
    this.clampWidth(this.currentWidth());
  }

  private observeWindowResize(): void {

    // Listen to window.resize
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reclampAfterResize();
      });
  }

  private get minPx(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 1;
    }
    return (this.minWidth / 100) * window.innerWidth;
  }

  private get maxPx(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 1;
    }
    return (this.maxWidth / 100) * window.innerWidth;
  }

  private get midPx(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 1;
    }
    const midPercent = (this.minWidth + this.maxWidth) / 2;
    return (midPercent / 100) * window.innerWidth;
  }

  protected onDragMoved(event: CdkDragMove): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const pointerX = event.pointerPosition.x;

    // ⚠️ Convert percentage → px bounds (based on window width)
    const minPx = (this.minWidth / 100) * window.innerWidth;
    const maxPx = (this.maxWidth / 100) * window.innerWidth;

    // Clamp pointer position to min/max limits
    const clamped = Math.max(minPx, Math.min(maxPx, pointerX));

    this.currentWidth.set(clamped);

    // Reset transform so the drag handle stays aligned
    const element = event.source.element.nativeElement;
    element.style.transform = 'none';
  }

  private clampWidth(px: number) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const minPx = (this.minWidth / 100) * window.innerWidth;
    const maxPx = (this.maxWidth / 100) * window.innerWidth;

    this.currentWidth.set(Math.max(minPx, Math.min(maxPx, px)));
  }

  private setInitialWidth() {
    this.currentWidth.set(this.midPx);
  }

  private reclampAfterResize() {
    const clamped = Math.max(this.minPx, Math.min(this.maxPx, this.currentWidth()));
    this.currentWidth.set(clamped);
  }

}
